import classnames from 'classnames';
import React, { forwardRef, Ref, useId, JSX } from 'react';

import { AttentionMessage } from '@eika/attention-message';
import { IconTypes } from '@eika/icon';

import RadioButton from './RadioButton';
import Tags from './Tags';
import { PanelButtonProps } from './Types';
import { toDataTrackIdFormat } from './utils/toDataTrackIdFormat';

const RadioButtonPanel = forwardRef(
    (
        {
            value,
            id,
            label,
            description,
            inputRef,
            selectedOption,
            onChange,
            name,
            describedBy,
            required,
            error,
            attentionMessage,
            tags,
            dataTrackId,
            dataTrackLinkname,
            grayed = false,
            locked = false,
            children,
            ...rest
        }: React.PropsWithChildren<PanelButtonProps>,
        ref: Ref<HTMLInputElement>,
    ): JSX.Element => {
        const generatedId = useId();
        const optionId = id ?? generatedId;
        const generatedAttentionMessageId = useId();

        const targetDataTrackLinkname = dataTrackLinkname ?? value ?? (typeof label === 'string' ? label : undefined);

        const attentionMessageId = attentionMessage ? generatedAttentionMessageId : undefined;
        return (
            <>
                <label
                    className={classnames(
                        'sgw-radio-button-panel-option',
                        'sgw-radio-button-panel-option__panel-wrapper',
                        {
                            'sgw-radio-button-panel-option__checked': value === selectedOption,
                            'sgw-radio-button-panel-option__error': error,
                            'with-attention-message': !!attentionMessage,
                            'sgw-radio-button-panel-option__grayed': grayed || locked,
                            'sgw-radio-button-panel-option__locked': locked,
                            'sgw-radio-button-panel-option__attention-label': tags,
                            disabled: rest.disabled,
                        },
                    )}
                >
                    <Tags tags={tags} />
                    <RadioButton
                        {...rest}
                        className={classnames('sgw-radio-button-option__input', rest.className)}
                        selectedOption={selectedOption}
                        error={error}
                        aria-describedby={attentionMessageId ?? describedBy}
                        required={required}
                        aria-invalid={error}
                        onChange={onChange}
                        name={name}
                        id={optionId}
                        dataTrackId={toDataTrackIdFormat(dataTrackId)}
                        dataTrackLinkname={dataTrackId ? toDataTrackIdFormat(targetDataTrackLinkname) : undefined}
                        ref={ref ?? inputRef}
                        value={value}
                        label={label}
                    />
                    {description && <span className="sgw-radio-button-panel-option-description">{description}</span>}
                    {children && <div className="sgw-radio-button-panel-option-children">{children}</div>}
                </label>
                {attentionMessage && (
                    <AttentionMessage
                        id={attentionMessageId}
                        className="sgw-radio-button-panel__attention-message"
                        {...attentionMessage}
                        type={attentionMessage.type ?? 'attention'}
                        text={attentionMessage.text}
                        icon={attentionMessage.icon ?? IconTypes.UTROPSTEGN_24}
                    />
                )}
            </>
        );
    },
);
export default RadioButtonPanel;
